import { Chip } from '@mui/material'
import React from 'react'
import { ReactElement, useCallback, useEffect } from 'react'
import type { ColumnInstance, FilterValue, IdType, TableInstance } from 'react-table'
import { FilterChipBarProps } from './TableProps'

const getFilterValue = (column: ColumnInstance<any>, filterValue: FilterValue) => {

  switch (column.filter) {
    case 'between':
      const min = filterValue[0]
      const max = filterValue[1]
      return min ? (max ? `${min}-${max}` : `>=${min}`) : `<=${max}`
  }

  if(column.filterOptions !== undefined && column.filterDisplayValue === undefined) {
    return column.filterOptions.find(x => x.value === filterValue)?.name;
  }

  if(typeof filterValue === "boolean" && (filterValue === true || filterValue === false)) {
    return filterValue === true ? "Yes" : "No";
  }

  return filterValue
}

export function FilterChipBar<T extends Record<string, unknown>>({
  instance,
  delayFilter,
  manualFilters
}: FilterChipBarProps<T>): ReactElement | null {

  const {
    allColumns,
    setFilter,
    state: { pageSize, pageIndex, filters, sortBy },
    pageChangeHandler
  } = instance

  //Update Chip Filters to table filters everytime there is a change 
  //to table filters only if delayFilter is not set to true.
  const [chipFilters, setChipFilters] = React.useState(filters);

  const handleDelete = useCallback(
    (column: ColumnInstance<T>) => {
      column.filterDisplayValue = undefined;
      setFilter(column.id as IdType<T>, undefined);
      
    },
    [setFilter]
  );

  useEffect(() => {

    if(delayFilter !== true) {

      //Only change it if it is different.
      if(filters !== chipFilters) {
        setChipFilters(filters);

        if(!!manualFilters && !!pageChangeHandler) {
          pageChangeHandler({
            currentPage: pageIndex, 
            pageSize: pageSize, 
            filters: filters, 
            sortBy: sortBy
          });
        }
      }
    }

  }, [delayFilter, filters])


  return Object.keys(chipFilters).length > 0 ? (
    <div className="chip-zone">
      <span className="filters-active-label">Active filters:</span>
      {chipFilters &&
        allColumns.map((column) => {
          const filter = chipFilters.find((f) => f.id === column.id)
          const value = column.filterDisplayValue ?? filter?.value

          return (
            value !== undefined && value !== null && value !== '' && (
              <Chip
                className="filter-chip"
                key={column.id}
                label={
                  <>
                    <span className="chip-label">{column.render('Header')}: </span>
                    {getFilterValue(column, value)}
                  </>
                }
                onDelete={() => handleDelete(column)}
                variant='outlined'
              />
            )
          )
        })}
    </div>
  ) : null
}
